// State management stores for VoterVerify
// Using zustand with standard immutable updates (no immer dependency)

// Internal imports
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { apiClient } from "@/lib/api-client";

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  constituencies: string[];
  permissions: Record<string, boolean>;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null; // Note: Token is stored in httpOnly cookie by backend, not in localStorage
  csrfToken: string | null; // CSRF token for form submissions
  _hydrate: () => void;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
}

interface AppState {
  sidebarOpen: boolean;
  theme: "light" | "dark" | "system";
  toggleSidebar: () => void;
  setTheme: (theme: "light" | "dark" | "system") => void;
}

interface VoterFilterState {
  filters: {
    search: string;
    constituency: string;
    baagam: string;
    verificationStatus: string;
    votingPreference: string;
    ageRange: [number, number];
  };
  setFilter: (key: string, value: any) => void;
  clearFilters: () => void;
  setFilters: (filters: Partial<VoterFilterState["filters"]>) => void;
}

// Helper function for middleware composition
const createAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      token: null, // Note: Token is stored in httpOnly cookie by backend, not in localStorage
      csrfToken: null, // CSRF token from login response

      // Check session by calling API (token comes from httpOnly cookie)
      _hydrate: async () => {
        console.log('[Auth Store] _hydrate called - checking session via API');
        try {
          const sessionData = await apiClient.get<{ authenticated: boolean; user?: any }>("/api/auth/session");

          if (sessionData.authenticated && sessionData.user) {
            console.log('[Auth Store] Session valid, setting user:', sessionData.user);
            set({
              user: sessionData.user,
              isAuthenticated: true,
              token: null, // Token is in httpOnly cookie, not stored here
            });
          } else {
            console.log('[Auth Store] Session invalid or expired');
            set({
              isAuthenticated: false,
              user: null,
              token: null,
            });
          }
        } catch (error) {
          console.error('Failed to hydrate auth state:', error);
          set({
            isAuthenticated: false,
            user: null,
            token: null,
          });
        }
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true });

        try {
          // Backend expects username, but frontend uses email field
          // Send email as username for login
          const authData = await apiClient.post<{ success: boolean; user?: User; csrfToken?: string }>("/api/auth/login", { username: email, password });

          set({
            user: authData.user || null,
            isAuthenticated: authData.success,
            token: null, // Token is stored in httpOnly cookie by backend
            csrfToken: authData.csrfToken || null, // Store CSRF token from response
            isLoading: false,
          });

          return authData.success;
        } catch (error) {
          console.error("Login error:", error);
          set({ isLoading: false });
          return false;
        }
      },

      logout: async () => {
        try {
          await apiClient.post("/api/auth/logout");

          set({
            user: null,
            isAuthenticated: false,
            token: null,
            csrfToken: null, // Clear CSRF token
          });
        } catch (error) {
          console.error("Logout error:", error);
        }
      },

      checkSession: async () => {
        try {
          // Use /api/auth/session endpoint (token from httpOnly cookie)
          const sessionData = await apiClient.get<{ authenticated: boolean; user?: any }>("/api/auth/session");

          if (sessionData.authenticated && sessionData.user) {
            set({
              user: sessionData.user,
              isAuthenticated: true,
            });
          } else {
            set({
              isAuthenticated: false,
              user: null,
              token: null,
            });
          }
        } catch (error) {
          console.error("Failed to check session:", error);
          set({
            isAuthenticated: false,
            user: null,
            token: null,
          });
        }
      },

      updateUser: (userData: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },
    })
  ),
  { name: "auth-store" }
);

export const useAuthStore = createAuthStore;

const createAppStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        sidebarOpen: true,
        theme: "system",

        toggleSidebar: () => {
          set((state) => ({ sidebarOpen: !state.sidebarOpen }));
        },

        setTheme: (theme: "light" | "dark" | "system") => {
          set({ theme });
        },
      }),
      {
        name: "app-storage",
      }
    ),
    { name: "app-store" }
  )
);

export const useAppStore = createAppStore;

const createVoterFilterStore = create<VoterFilterState>()(
  devtools(
    persist(
      (set) => ({
        filters: {
          search: "",
          constituency: "",
          baagam: "",
          verificationStatus: "",
          votingPreference: "",
          ageRange: [18, 100],
        },

        setFilter: (key: string, value: any) => {
          set((state) => ({
            filters: { ...state.filters, [key]: value },
          }));
        },

        clearFilters: () => {
          set({
            filters: {
              search: "",
              constituency: "",
              baagam: "",
              verificationStatus: "",
              votingPreference: "",
              ageRange: [18, 100],
            },
          });
        },

        setFilters: (filters: Partial<VoterFilterState["filters"]>) => {
          set((state) => ({
            filters: { ...state.filters, ...filters },
          }));
        },
      }),
      {
        name: "voter-filters-storage",
      }
    ),
    { name: "voter-filters-store" }
  )
);

export const useVoterFilterStore = createVoterFilterStore;

// Selectors for efficient re-renders
export const useAuth = () => useAuthStore((state) => state);
export const useApp = () => useAppStore((state) => state);
export const useVoterFilters = () => useVoterFilterStore((state) => state);

// Derived selectors
export const useCurrentUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useUserPermissions = () =>
  useAuthStore((state) => state.user?.permissions || {});
export const useUserConstituencies = () =>
  useAuthStore((state) => state.user?.constituencies || []);
export const useUserRole = () =>
  useAuthStore((state) => state.user?.role || "");
